import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { getGameColor, getGameName } from "../constants/theme";
import { toast } from "sonner";
import {
  Trophy, Gamepad2, Calendar, MapPin, Users, CalendarDays, Plus, CheckCircle, Bookmark, PenLine,
} from "lucide-react";

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function PlayerMyEventsPage() {
  const { user, canAccessAdmin } = useAuth();
  const [data, setData] = useState({ registered: [], created_events: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("registered");

  useEffect(() => {
    api.get("/users/me/tournaments")
      .then(r => {
        setData({
          registered: r.data.registered || [],
          created_events: r.data.created_events || [],
        });
      })
      .catch(() => toast.error("Erreur chargement"))
      .finally(() => setLoading(false));
  }, []);

  const list = tab === "registered" || !canAccessAdmin ? data.registered : data.created_events;

  const EventCard = ({ item }) => {
    const isAvailability = item.type === "availability";
    const isInterested = item._source === "interested";
    const gameColor = getGameColor(item.game);
    const link = isAvailability
      ? `/player/event/party/${item.id}`
      : `/player/event/tournament/${item.id}`;

    return (
      <Card className="bg-[#1a1a1a] border-[#333] hover:border-[#FF6B35]/30 transition-all" data-testid={`event-${item.id}`}>
        <CardContent className="p-0">
          <div className="flex">
            <div className="w-1 rounded-l-xl flex-shrink-0" style={{ backgroundColor: isAvailability ? "#4ECDC4" : gameColor }} />
            <Link to={link} className="flex-1 p-3 sm:p-4 block">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <Badge style={{ backgroundColor: `${gameColor}20`, color: gameColor }} className="text-[10px] sm:text-xs gap-1">
                      {isAvailability ? <Gamepad2 className="w-3 h-3" /> : <Trophy className="w-3 h-3" />}
                      {getGameName(item.game)}
                    </Badge>
                    {item.format && <Badge className="bg-[#252525] text-[#B0B0B0] text-[10px] sm:text-xs">{item.format}</Badge>}
                    {isAvailability && <Badge className="bg-[#4ECDC4]/10 text-[#4ECDC4] text-[10px]">Partie</Badge>}
                    {isInterested && (
                      <Badge className="bg-[#FF6B35]/10 text-[#FF6B35] text-[10px] gap-0.5">
                        <Bookmark className="w-2.5 h-2.5" />Liste
                      </Badge>
                    )}
                    {item.status === "in_progress" && (
                      <Badge className="bg-green-500/10 text-green-400 text-[10px] gap-0.5 animate-pulse">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />En cours
                      </Badge>
                    )}
                  </div>
                  <p className="text-white font-semibold text-sm sm:text-base truncate">{item.name}</p>
                  <div className="flex items-center gap-2 sm:gap-3 mt-1.5 text-[10px] sm:text-xs text-[#666] flex-wrap">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(item.date)}</span>
                    {(item.venue?.city || item.location?.city) && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.venue?.city || item.location?.city}</span>
                    )}
                    {item.participants_count !== undefined && (
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{item.participants_count}{item.capacity ? `/${item.capacity}` : ""}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div data-testid="player-my-events">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">{canAccessAdmin ? "Mon Agenda" : "Mes événements"}</h1>
          <p className="text-[#666] text-xs sm:text-sm mt-0.5">{canAccessAdmin ? "Vos inscriptions et créations" : "Les tournois et évènements où vous êtes inscrit"}</p>
        </div>
        {canAccessAdmin && (
        <Link to="/player/create" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white text-xs sm:text-sm font-medium transition-colors" data-testid="create-event-link">
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Créer</span>
        </Link>
        )}
      </div>

      {canAccessAdmin && (
      <div className="flex gap-2 mb-4 sm:mb-6">
        <button onClick={() => setTab("registered")} data-testid="tab-inscrit"
          className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
            tab === "registered" ? "bg-[#FF6B35]/15 text-[#FF6B35] ring-1 ring-[#FF6B35]/30" : "bg-[#1a1a1a] text-[#B0B0B0] hover:bg-[#252525]"
          }`}>
          <CheckCircle className="w-3.5 h-3.5" />
          Inscrit ({data.registered.length})
        </button>
        <button onClick={() => setTab("created")} data-testid="tab-created"
          className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
            tab === "created" ? "bg-[#FF6B35]/15 text-[#FF6B35] ring-1 ring-[#FF6B35]/30" : "bg-[#1a1a1a] text-[#B0B0B0] hover:bg-[#252525]"
          }`}>
          <PenLine className="w-3.5 h-3.5" />
          Mes créations ({data.created_events.length})
        </button>
      </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" /></div>
      ) : list.length === 0 ? (
        <div className="text-center py-16 text-[#666]">
          {tab === "registered" ? <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" /> : <PenLine className="w-12 h-12 mx-auto mb-3 opacity-50" />}
          <p className="font-medium text-white mb-1">{tab === "registered" ? "Aucune inscription" : "Aucun événement créé"}</p>
          <p className="text-sm">{tab === "registered" ? "Découvrez des tournois et inscrivez-vous !" : "Créez votre premier tournoi ou partie"}</p>
          <Link to={tab === "registered" ? "/player/discover" : "/player/create"} className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-sm font-medium hover:bg-[#E55A25] transition-colors">
            {tab === "registered" ? "Découvrir" : "Créer"}
          </Link>
        </div>
      ) : (
        <div className="grid gap-3">
          {list.map(item => <EventCard key={`${item.type || "t"}-${item.id}`} item={item} />)}
        </div>
      )}
    </div>
  );
}
